import TestAiModelConnection from '@Application/Ai/AiConfiguration/Models/Commands/TestAiModelConnection';
import testAiModelConnection from '@Application/Ai/AiConfiguration/Models/testAiModelConnection';
import { PermissionEnum } from '@Application/Security/Roles';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import { openModelSidebar } from '@UI/Pages/Administration/AiEngines/Signals/modelSidebarSignal';
import { setModelToDelete } from '@UI/Pages/Administration/AiEngines/Signals/modelToDeleteSignal';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type AiModelView from '@UI/Store/AiConfiguration/Views/AiModelView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly model: AiModelView;
};

/**
 * Leaf action component: it opens the edit sidebar, requests the delete confirmation and dispatches
 * the connection test itself, purely through the signals' setters and `useDispatch` — no callback
 * passed up in props. "Modifier" requires `AI_MODEL_WRITE`, "Supprimer" requires `AI_MODEL_DELETE` —
 * the two used to be conflated under a single manage permission, now split by object. "Tester"
 * requires `AI_MODEL_WRITE` or `AI_ASSIGNMENT_WRITE`, mirroring exactly the server guard on
 * this route (`@RequireAnyPermission(AI_MODEL_WRITE, AI_ASSIGNMENT_WRITE)`). The test always targets
 * the model's first declared usage — a model with none cannot be tested, there being no usage to test
 * against. Buttons carry visible text, rendered as `children` rather than `label`: the kit resolves
 * the accessible name from `label` first, which would shadow the explicit `aria-label` naming the
 * model — `children` leaves that resolution to `aria-label`.
 *
 * The outer `<div>` is this card's fourth subgrid track, not decoration: it must render even with no
 * granted permission, or the card falls one track short of its siblings and drifts out of alignment.
 */
const ModelCardActions: React.FC<Props> = ({ model }) => {
	const dispatch = useDispatch();
	const canEdit = useHasPermission(PermissionEnum.AI_MODEL_WRITE);
	const canDelete = useHasPermission(PermissionEnum.AI_MODEL_DELETE);
	const canTest = useHasPermission(
		[PermissionEnum.AI_MODEL_WRITE, PermissionEnum.AI_ASSIGNMENT_WRITE],
		'any',
	);
	const testableUsage = model.usages[0] ?? null;

	const handleEditClick = React.useCallback(() => {
		openModelSidebar(model.id);
	}, [model.id]);

	const handleDeleteClick = React.useCallback(() => {
		setModelToDelete(model.id);
	}, [model.id]);

	const handleTestClick = React.useCallback(() => {
		if (testableUsage === null) {
			return;
		}

		dispatch(
			testAiModelConnection(new TestAiModelConnection(model.accountId, model.id, testableUsage)),
		).catch(ignoreHandledError);
	}, [dispatch, model.accountId, model.id, testableUsage]);

	if (!canEdit && !canDelete && !canTest) {
		return <div css={actionsRowStyle} />;
	}

	return (
		<div css={actionsRowStyle}>
			{canEdit ? (
				<Button
					type="button"
					size="sm"
					severity="secondary"
					aria-label={`Modifier le modèle ${model.label}`}
					onClick={handleEditClick}
				>
					Modifier
				</Button>
			) : null}
			{canTest ? (
				<Button
					type="button"
					size="sm"
					severity="secondary"
					aria-label={`Tester la connexion du modèle ${model.label}`}
					disabled={testableUsage === null}
					onClick={handleTestClick}
				>
					Tester
				</Button>
			) : null}
			{canDelete ? (
				<Button
					type="button"
					size="sm"
					severity="danger"
					aria-label={`Supprimer le modèle ${model.label}`}
					onClick={handleDeleteClick}
				>
					Supprimer
				</Button>
			) : null}
		</div>
	);
};

export default ModelCardActions;
